import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import moment from 'moment';

import { useAuth } from 'contexts/auth';
import { Box, Button, Stack, Text, MenuItem, Select, ResponsiveDialog } from 'shell/components';
import ltiService from 'services/lti';

const GroupSync = ({ device, open, sendGroupSetUpdate, onClose }) => {
    const { auth } = useAuth();

    const [loading, setLoading] = useState(false);
    const [syncedDate, setSyncedDate] = useState('Syncing...');
    const [groupSets, setGroupSets] = useState();
    const [selectedGroupSet, setSelectedGroupSet] = useState();

    const handleInputs = (value) => {
        setSelectedGroupSet(value);
    };

    const handleClose = () => {
        if (loading) return;
        setLoading(false);
        setSelectedGroupSet(null);
        onClose();
    };

    const handleGroupSync = async () => {
        setLoading(true);
        await sendGroupSetUpdate(selectedGroupSet);
        setLoading(false);
        handleClose();
    };

    const getGroupSetRequest = async () => {
        const getGroupSets = await ltiService.getGroupSets({
            course_id: auth.course_id,
        });

        if (getGroupSets?.length > 0) {
            let groupIndex = 0;
            let lastIndex = 0;
            let groupSets = [];
            let title = '';
            getGroupSets.map((group) => {
                let isNextGroupSet = false;
                if (groupSets.length === 0) {
                    title = title + group.context.title;
                    // When there is no group set
                    groupSets = [
                        {
                            title,
                            value: {
                                key: group.context.id,
                                data: [{ groupNumber: 1, members: group.members, name: group.context.title }],
                            },
                        },
                    ];
                } else {
                    for (let i = lastIndex; i < groupSets.length; i++) {
                        const data = groupSets[i]?.value?.data;
                        const hasMember = group.members.find((groupMember) => {
                            let result = false;
                            for (let j = 0; j < data.length; j++) {
                                const members = data[j].members;
                                const containMember = members?.find((member) => groupMember.user_id === member.user_id);
                                if (containMember) {
                                    result = true;
                                    break;
                                }
                            }
                            return result;
                        });
                        if (hasMember) {
                            isNextGroupSet = true;
                            lastIndex = i + 1;
                            break;
                        }
                    }

                    if (!isNextGroupSet) {
                        title = title + ', ' + group.context.title;
                        // When the group data is for already existing group set
                        const thisGroupIndex = groupSets[groupIndex]?.value?.data?.length + 1;
                        const copyExistingData = groupSets[groupIndex].value.data;

                        groupSets[groupIndex] = {
                            title,
                            value: {
                                key: group.context.id,
                                data: [
                                    ...copyExistingData,
                                    {
                                        groupNumber: thisGroupIndex,
                                        members: group.members,
                                        name: group.context.title,
                                    },
                                ],
                            },
                        };
                    } else {
                        title = group.context.title;
                        // When there is a new group set
                        groupSets = [
                            ...groupSets,
                            {
                                title,
                                value: {
                                    key: group.context.id,
                                    data: [{ groupNumber: 1, members: group.members, name: group.context.title }],
                                },
                            },
                        ];

                        groupIndex = groupIndex + 1;
                    }
                    isNextGroupSet = false;
                }

                return;
            });
            setSyncedDate(moment().format('MMM DD [at] hh:mm a'));
            return setGroupSets(groupSets);
        }
    };

    useEffect(() => {
        if (open) {
            (async () => {
                await getGroupSetRequest();
            })();
        }
    }, [open]);

    return (
        <ResponsiveDialog open={open} onClose={handleClose} closable={false}>
            <Stack spacing={7}>
                <HeaderContainer alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'}>Select the Group Set You Would Like to Resync from Canvas</Text>
                </HeaderContainer>

                <DropDownStack spacing={1}>
                    <FormatSelect
                        value={selectedGroupSet || ''}
                        label={'Group Set'}
                        labelId={'group-set-id'}
                        onChange={(e) => handleInputs(e.target.value)}
                        required
                    >
                        {groupSets ? (
                            groupSets?.map((groupSet) => {
                                return (
                                    <MenuItem key={groupSet.value.key} value={groupSet?.value}>
                                        {groupSet.title}
                                    </MenuItem>
                                );
                            })
                        ) : (
                            <MenuItem value={'No Group Set'}>
                                No group sets found. Please create group sets on Canvas and refresh the page.
                            </MenuItem>
                        )}
                    </FormatSelect>
                    <Text variant={'p3'} color={'light'}>{`Last synced: ${syncedDate}`}</Text>
                </DropDownStack>
                <Stack
                    direction={'row-reverse'}
                    justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                    spacing={4}
                >
                    <Button disabled={!Boolean(selectedGroupSet)} loading={loading} onClick={handleGroupSync}>
                        Confirm Selection
                    </Button>
                    <Button variant={'secondary'} onClick={handleClose}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </ResponsiveDialog>
    );
};

const HeaderContainer = styled(Stack)(({ theme }) => {
    return {
        marginBottom: theme.spacing(2),
    };
});

const DropDownStack = styled(Box)(({ device, theme }) => {
    return {
        margin: theme.spacing(4, 0),
        width: '100%',
        maxHeight: 240,
        overflowY: 'auto',
        ...(device === 'mobile' && {
            width: '100%',
        }),
    };
});

const FormatSelect = styled(Select)(() => {
    return {
        width: '100%',
    };
});

export default GroupSync;
